import { useQueryClient } from "@tanstack/react-query"
import { useState, useContext } from "react"
import Header from "../../../layouts/Header"
import BackIcon from "../../../components/BackIcon"
import { useNavigate } from "react-router-dom"
import { Box, Paper, TextField, Tooltip, IconButton, Grid, Typography, Autocomplete, MenuItem } from "@mui/material"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import CustomBackdrop from "../../../components/CustomBackdrop"
import { useMutation, useQuery, api, showWarning } from "../../../api"
import { GlobalContext } from "../../../contexts/GlobalContext"
import getIconComponent from "../../utils/getIconComponent"
import Save from "@mui/icons-material/Save"

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

const StyledRoot = {
  width: "100%",
  maxWidth: "1200px",
  margin: "0 auto",
  padding: "20px",
  backgroundColor: "#f5f7fa",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}

const CrearTomaFisica = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { selectedMenuInfo } = useContext(GlobalContext)

  // AJUSTE: Transformamos los campos a Array para soportar multiselección
  const [formData, setFormData] = useState({
    bodegas: [],
    tomdescripcion: "",
    tomtipo: "TOTAL",
    marcas: [],
    lineas: [],
    operarios: [],
  })

  // --- LÓGICA DEFENSIVA PARA CATÁLOGOS DE CREACIÓN ---
  const { data: rawCatalogos, isLoading: isLoadingCatalogos } = useQuery({
    queryKey: ["catalogosGeneracionTomo"],
    queryFn: async () => {
      try {
        const response = await api.post("/TomaFisica/getCatalogosGeneracionTomo", {})
        return response?.data?.data || response?.data || {}
      } catch (error) {
        console.error("Error al cargar catálogos:", error)
        return {}
      }
    },
    refetchOnWindowFocus: false,
  })

  const catalogosBase = rawCatalogos || {}

  const rawBodegas = catalogosBase?.bodegas || catalogosBase?.data?.bodegas || []
  const listaBodegas = Array.isArray(rawBodegas) ? rawBodegas : []

  const rawMarcas = catalogosBase?.marcas || catalogosBase?.data?.marcas || []
  const listaMarcas = Array.isArray(rawMarcas) ? rawMarcas : []

  const rawFamilias = catalogosBase?.familias || catalogosBase?.data?.familias || []
  const listaFamilias = Array.isArray(rawFamilias) ? rawFamilias : []

  const rawOperarios = catalogosBase?.operarios || catalogosBase?.data?.operarios || []
  const listaOperarios = Array.isArray(rawOperarios) ? rawOperarios : []
  // --- FIN LÓGICA DEFENSIVA ---

  const {
    mutateAsync: SaveGenerarTomo,
    isPending: isSavingCreacion,
    isError,
  } = useMutation({
    queryKey: ["isCreatingTomo"],
    fn: async (data) => {
      const response = await api.post("/TomaFisica/generarTomo", data)
      return response.data
    },
    showError: "modal",
    showSuccess: "toast",
    onSuccess: async () => {
      await queryClient.invalidateQueries()
      navigate(-1)
    },
  })

  const handleInputChange = (field, value) => {
    let finalValue = typeof value === "string" ? value.toUpperCase() : value

    if (field === "tomdescripcion") finalValue = finalValue.slice(0, 100)

    setFormData((prev) => ({ ...prev, [field]: finalValue }))

    // Limpiar dependencias dinámicas si cambia a TOTAL
    if (field === "tomtipo" && finalValue === "TOTAL") {
      setFormData((prev) => ({ ...prev, marcas: [], lineas: [] }))
    }
  }

  const handleSubmit = async (e) => {
    if (e) e.preventDefault()

    // Validaciones del frontend
    if (formData.bodegas.length === 0)
      return showWarning("Debe seleccionar al menos una Bodega para realizar el inventario.")
    if (!formData.tomdescripcion.trim()) return showWarning("La Descripción del Tomo es obligatoria.")
    if (formData.operarios.length === 0) return showWarning("Debe asignar al menos un operario al tomo.")

    // Validación lógica para parciales
    if (formData.tomtipo === "PARCIAL" && formData.marcas.length === 0 && formData.lineas.length === 0) {
      return showWarning("Al ser una toma PARCIAL, debe filtrar por al menos una Marca o Familia.")
    }

    // Formateo del Payload: Extraemos IDs y enviamos Estructuras
    const dataToSend = {
      bodegas: formData.bodegas.map((b) => b.id),
      tomdescripcion: formData.tomdescripcion,
      tomtipo: formData.tomtipo,
      marcas: formData.marcas.map((m) => m.id),
      // Enviamos el objeto con el tipo M/T para que el backend lo reconozca
      lineas: formData.lineas.map((l) => ({ id: l.id, tipo: l.tipo })),
      operarios: formData.operarios.map((op) => op.id),
    }

    await SaveGenerarTomo(dataToSend)
  }

  const guardarAction = selectedMenuInfo?.data?.barraAcciones?.find((action) => action?.acccaption === "GRABAR")

  const toolbarActions = guardarAction
    ? [
        {
          label: guardarAction.acccaption,
          key: guardarAction.acccaption,
          icon: getIconComponent(guardarAction.accnameicono, guardarAction.acctipoico),
        },
      ]
    : [{ label: "Guardar", key: "GUARDAR", icon: <Save /> }]

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />
        <Box sx={{ mb: 2 }}>
          {toolbarActions.map((action) => (
            <Tooltip title={action.label} key={action.key}>
              <IconButton
                onClick={handleSubmit}
                disabled={isSavingCreacion || isLoadingCatalogos}
                sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}
              >
                {action.icon}
              </IconButton>
            </Tooltip>
          ))}
        </Box>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
          }}
        >
          <b>Generar Nuevo Tomo Físico</b>
        </div>

        <CustomBackdrop isLoading={isSavingCreacion || isLoadingCatalogos} />

        <Box sx={StyledRoot}>
          <Paper
            elevation={3}
            sx={{ p: 4, borderRadius: 3, background: "white" }}
            component="form"
            onSubmit={handleSubmit}
          >
            <Typography variant="h6" color="primary" gutterBottom>
              Parámetros de Planificación del Inventario
            </Typography>

            <Grid container spacing={3}>
              {/* COMBO MÚLTIPLE DE BODEGAS */}
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  multiple
                  options={listaBodegas}
                  getOptionLabel={(option) => option.label || ""}
                  value={formData.bodegas}
                  onChange={(event, newValue) => handleInputChange("bodegas", newValue)}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Bodegas a inventariar *"
                      placeholder="Seleccione..."
                      InputLabelProps={{ shrink: true }}
                      error={isError && formData.bodegas.length === 0}
                    />
                  )}
                />
              </Grid>

              {/* TIPO DE TOMA */}
              <Grid item xs={12} sm={3}>
                <TextField
                  select
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Tipo de Toma *"
                  value={formData.tomtipo}
                  onChange={(e) => handleInputChange("tomtipo", e.target.value)}
                >
                  <MenuItem value="TOTAL">TOTAL (Toda la bodega)</MenuItem>
                  <MenuItem value="PARCIAL">PARCIAL (Por Marca/Familia)</MenuItem>
                </TextField>
              </Grid>

              {/* DESCRIPCIÓN TOMO */}
              <Grid item xs={12} sm={5}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Descripción General *"
                  value={formData.tomdescripcion}
                  onChange={(e) => handleInputChange("tomdescripcion", e.target.value)}
                  error={isError && !formData.tomdescripcion}
                  inputProps={{ maxLength: 100 }}
                  placeholder="Ej: INVENTARIO DE FIN DE MES"
                />
              </Grid>

              {/* RENDERIZADO CONDICIONAL DE FILTROS PARCIALES MÚLTIPLES */}
              {formData.tomtipo === "PARCIAL" && (
                <>
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      multiple
                      options={listaMarcas}
                      getOptionLabel={(option) => option.label || ""}
                      value={formData.marcas}
                      onChange={(event, newValue) => handleInputChange("marcas", newValue)}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Filtro por Marcas"
                          placeholder="Agregue marcas a auditar..."
                          InputLabelProps={{ shrink: true }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      multiple
                      options={listaFamilias}
                      getOptionLabel={(option) => option.label || ""}
                      value={formData.lineas}
                      onChange={(event, newValue) => handleInputChange("lineas", newValue)}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Filtro por Familias (Líneas)"
                          placeholder="Agregue las familias a auditar..."
                          InputLabelProps={{ shrink: true }}
                        />
                      )}
                    />
                  </Grid>
                </>
              )}

              {/* ASIGNACIÓN DE OPERARIOS MÚLTIPLES */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" color="primary" sx={{ mt: 2, mb: 1, fontWeight: "bold" }}>
                  Asignación de Personal
                </Typography>
                <Autocomplete
                  multiple
                  options={listaOperarios}
                  getOptionLabel={(option) => option.label || ""}
                  value={formData.operarios}
                  onChange={(event, newValue) => handleInputChange("operarios", newValue)}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Operarios Asignados *"
                      placeholder="Seleccione los operarios que ejecutarán el conteo en sus móviles..."
                      InputLabelProps={{ shrink: true }}
                      error={isError && formData.operarios.length === 0}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default CrearTomaFisica
